import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";
import type {
  AudienceAction,
  AudienceDto,
  AudienceMembershipDto,
  DeliveryDto,
  ListParams,
  NotificationAction,
  NotificationDeliveryAction,
  NotificationDto,
  NotificationTemplateAction,
  PaginationMeta,
  RequestMeta,
  TemplateDto,
} from "./types";

const BASE = `${API_VERSION_PREFIX}/hq/notifications`;

function toQuery(params: ListParams = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export function createMarketingApi(client: ApiClient) {
  return {
    listTemplates(params: ListParams = {}) {
      return client.get<{
        templates: TemplateDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/templates${toQuery(params)}`);
    },
    getTemplate(id: string) {
      return client.get<{ template: TemplateDto; meta: RequestMeta }>(
        `${BASE}/templates/${id}`,
      );
    },
    createTemplate(body: Record<string, unknown>) {
      return client.post<{ template: TemplateDto; meta: RequestMeta }>(
        `${BASE}/templates`,
        body,
        newIdempotencyKey(),
      );
    },
    applyTemplateAction(
      id: string,
      action: NotificationTemplateAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/templates/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listAudiences(params: ListParams = {}) {
      return client.get<{
        audiences: AudienceDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/audiences${toQuery(params)}`);
    },
    getAudience(id: string) {
      return client.get<{ audience: AudienceDto; meta: RequestMeta }>(
        `${BASE}/audiences/${id}`,
      );
    },
    createAudience(body: Record<string, unknown>) {
      return client.post<{ audience: AudienceDto; meta: RequestMeta }>(
        `${BASE}/audiences`,
        body,
        newIdempotencyKey(),
      );
    },
    applyAudienceAction(
      id: string,
      action: AudienceAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/audiences/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
    listMemberships(audienceId: string, params: ListParams = {}) {
      return client.get<{
        memberships: AudienceMembershipDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/audiences/${audienceId}/memberships${toQuery(params)}`);
    },
    addMembership(audienceId: string, body: Record<string, unknown>) {
      return client.post(
        `${BASE}/audiences/${audienceId}/memberships`,
        body,
        newIdempotencyKey(),
      );
    },
    excludeMembership(
      audienceId: string,
      membershipId: string,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/audiences/${audienceId}/memberships/${membershipId}/exclude`,
        body,
        newIdempotencyKey(),
      );
    },

    listNotifications(params: ListParams = {}) {
      return client.get<{
        notifications: NotificationDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/notifications${toQuery(params)}`);
    },
    getNotification(id: string) {
      return client.get<{ notification: NotificationDto; meta: RequestMeta }>(
        `${BASE}/notifications/${id}`,
      );
    },
    createNotification(body: Record<string, unknown>) {
      return client.post<{ notification: NotificationDto; meta: RequestMeta }>(
        `${BASE}/notifications`,
        body,
        newIdempotencyKey(),
      );
    },
    applyNotificationAction(
      id: string,
      action: NotificationAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/notifications/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },

    listDeliveries(params: ListParams = {}) {
      return client.get<{
        deliveries: DeliveryDto[];
        pagination: PaginationMeta;
        meta: RequestMeta;
      }>(`${BASE}/deliveries${toQuery(params)}`);
    },
    getDelivery(id: string) {
      return client.get<{ delivery: DeliveryDto; meta: RequestMeta }>(
        `${BASE}/deliveries/${id}`,
      );
    },
    applyDeliveryAction(
      id: string,
      action: NotificationDeliveryAction,
      body: Record<string, unknown>,
    ) {
      return client.post(
        `${BASE}/deliveries/${id}/actions/${action}`,
        body,
        newIdempotencyKey(),
      );
    },
  };
}

export type MarketingApi = ReturnType<typeof createMarketingApi>;
